import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, BadgeCheck, Mail, FileSpreadsheet, Sparkles, FileText, Upload,
  ArrowRight, GitBranch, ShieldCheck, Lock, Loader2, ScanSearch, ReceiptText, FileCode2,
} from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// The written method is served from an authenticated endpoint so it never sits in the public
// bundle. Visitors who are not signed in see a locked preview instead.
const ICONS = { Search, BadgeCheck, Mail, FileSpreadsheet, Sparkles, FileText, Upload, ArrowRight, GitBranch, ScanSearch };
const contractorFlow = [Search, BadgeCheck, Mail, GitBranch];
const vendorFlow = [Upload, FileSpreadsheet, Sparkles, ScanSearch, FileText, ArrowRight];
const CONTACT_EMAIL = "admin@matiqs.net";

const LockedRow = ({ Icon, last, wide }) => (
  <li className="flex gap-4" aria-hidden="true">
    <div className="shrink-0 flex flex-col items-center">
      <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      {!last && <div className="w-px flex-1 bg-border mt-2" />}
    </div>
    <div className="pb-1 flex-1 space-y-2 pt-1.5">
      <div className="h-3.5 rounded bg-foreground/15" style={{ width: wide ? "62%" : "48%" }} />
      <div className="h-2.5 rounded bg-foreground/10 w-full" />
      <div className="h-2.5 rounded bg-foreground/10" style={{ width: wide ? "82%" : "70%" }} />
    </div>
  </li>
);

const Step = ({ step, index, last }) => {
  const Icon = ICONS[step.icon] || Sparkles;
  return (
    <motion.li
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06 }}
      className="flex gap-4"
      data-testid={`step-${index + 1}`}
    >
      <div className="shrink-0 flex flex-col items-center">
        <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        {!last && <div className="w-px flex-1 bg-border mt-2" />}
      </div>
      <div className="pb-2 flex-1">
        <h3 className="text-sm md:text-base font-bold text-slate-900 dark:text-slate-100">
          <span className="text-muted-foreground font-mono text-xs mr-2">{index + 1}</span>
          {step.title}
        </h3>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{step.body}</p>
      </div>
    </motion.li>
  );
};

const Card = ({ label, tint, testid, children }) => (
  <div data-testid={testid} className="relative bg-card border border-border rounded-2xl p-6 md:p-8 overflow-hidden">
    <div className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] mb-6 ${tint}`}>
      {label}
    </div>
    {children}
  </div>
);

const LockedTrack = ({ label, flow, tint, testid }) => (
  <Card label={label} tint={tint} testid={testid}>
    <ol data-testid={`${testid}-locked`} className="space-y-6 blur-[5px] select-none pointer-events-none">
      {flow.map((Icon, i) => (
        <LockedRow key={i} Icon={Icon} last={i === flow.length - 1} wide={i % 2 === 0} />
      ))}
    </ol>

    <div className="absolute inset-0 flex items-center justify-center p-6 bg-card/60 backdrop-blur-[2px]">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-sm text-center bg-card border border-border rounded-2xl shadow-xl shadow-slate-900/10 px-6 py-7"
      >
        <div className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center mx-auto">
          <Lock className="w-5 h-5" />
        </div>
        <h3 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 mt-4">
          Sign in to read the method
        </h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          The step-by-step workflow is kept behind a login to protect it from being copied. Create a free
          account and it opens straight away — or talk to us and we will walk you through it.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
          <Link to="/login?mode=register" data-testid={`${testid}-signup`} className="sm:flex-1">
            <Button className="h-10 w-full font-semibold">Start free</Button>
          </Link>
          <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Matiqs — how it works (${label})`)}`}
             data-testid={`${testid}-contact`} className="sm:flex-1">
            <Button variant="outline" className="h-10 w-full font-semibold gap-2">
              <Mail className="w-4 h-4" /> Contact us
            </Button>
          </a>
        </div>
      </motion.div>
    </div>
  </Card>
);

export default function HowItWorks() {
  const { user, ready } = useAuth();
  const [steps, setSteps] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      setSteps(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get("/how-it-works")
      .then(({ data }) => setSteps(data))
      .catch(() => setSteps(null))
      .finally(() => setLoading(false));
  }, [user, ready]);

  const tracks = [
    { key: "contractor", label: "For contractors", testid: "track-contractors", flow: contractorFlow,
      tint: "bg-accent text-primary border border-primary/20" },
    { key: "vendor", label: "For vendors", testid: "track-vendors", flow: vendorFlow,
      tint: "bg-brand-soft text-brand border border-brand/20" },
  ];

  return (
    <PublicShell
      eyebrow="How it works"
      title="From the contractor's BOQ to a priced quotation."
      intro="Matiqs is a verified vendor network with automatic quotation building on top. Contractors find the right supplier; vendors price the BOQ in minutes instead of days."
    >
      {loading ? (
        <div data-testid="how-loading" className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" /> Opening the workflow…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {tracks.map((t) =>
            steps?.[t.key]?.length ? (
              <Card key={t.key} label={t.label} tint={t.tint} testid={t.testid}>
                <ol data-testid={`${t.testid}-steps`} className="space-y-6">
                  {steps[t.key].map((s, i) => (
                    <Step key={i} step={s} index={i} last={i === steps[t.key].length - 1} />
                  ))}
                </ol>
              </Card>
            ) : (
              <LockedTrack key={t.key} {...t} />
            )
          )}
        </div>
      )}

      <div className="mt-10 bg-card border border-border rounded-2xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-brand" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100">What “verified” means here</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Vendors upload their trade licence, VAT/TRN certificate, insurance and any other certificates. Our AI reads
              each document, pulls out the expiry date and flags it Valid, Expiring soon or Expired. A vendor only keeps
              the “Documents updated” badge while nothing has expired — so the badge you see on a listing is live, not a
              one-off check. Matiqs does not certify workmanship or act as a party to your contract.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 bg-card border border-border rounded-2xl p-6 md:p-8" data-testid="how-einvoicing">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center shrink-0">
            <ReceiptText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">UAE FTA · Peppol PINT-AE</div>
            <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100 mt-1">E-invoicing: from Contract BOQ to a compliant tax invoice</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              A standalone, ERP-alternative service. Upload your Contract BOQ once, then invoice every
              Interim Payment Certificate (IPC) — reconciled against the contract and exported in every
              format your Accredited Service Provider needs.
            </p>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mt-5">
              {[
                [Upload, "Set up the contract once", "Add the contract — buyer, Peppol ID, contract date — then enter your Contract BOQ at a high level (Line #, Description, Original/Approved Scope in AED), or upload it and let Matiqs auto-fill from the summary."],
                [FileSpreadsheet, "Build each IPC", "For every payment certificate, enter the cumulative % complete against each BOQ line, plus any Advance Payment or Retention Release item — or upload the IPC and auto-fill."],
                [ShieldCheck, "Run review", "Matiqs computes this period's values from the cumulative % (less what earlier IPCs billed), applies 5% VAT, and flags anything that needs attention."],
                [FileCode2, "Generate & submit", "Get Excel, CSV, a certificate PDF and an optional Peppol UBL 2.1 XML — ready for your ASP."],
              ].map(([Icon, title, body], i) => (
                <li key={title} className="flex gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                      <span className="text-muted-foreground font-mono text-xs mr-2">{i + 1}</span>{title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Link to="/einvoicing"><Button data-testid="how-einvoicing-cta" className="h-10 font-semibold gap-2">Explore e-invoicing <ArrowRight className="w-4 h-4" /></Button></Link>
              <span className="text-xs text-muted-foreground">Matiqs Qonnect is a lightweight, affordable ERP alternative that partners with Approved Service Providers (who transmit to the FTA) to help SMBs/SMEs save on e-invoicing; connected submission via our Approved ASP is coming soon.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-border bg-secondary/50 p-6 md:p-8">
        <div>
          <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100">
            {user ? "Put it to work" : "Ready to try it?"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {user
              ? "Head to your dashboard and price a BOQ, or browse the network and send an RFQ."
              : "Browse the network as a contractor, or sign in as a vendor and price your first BOQ."}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to="/"><Button data-testid="how-cta-marketplace" variant="outline" className="h-11 font-semibold">Browse vendors</Button></Link>
          <Link to={user ? (user.account_type === "contractor" ? "/marketplace" : user.account_type === "einvoice" ? "/einvoicing/app" : "/dashboard") : "/login"}>
            <Button data-testid="how-cta-signin" className="h-11 font-semibold gap-2 bg-brand text-brand-foreground hover:bg-brand/90">
              {user ? "Go to dashboard" : "Vendor sign in"} <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </PublicShell>
  );
}
